(() => {
  'use strict';

  const storagePrefix = 'pino:navigation-scroll:v1:';
  const maximumRecordAge = 2 * 60 * 60 * 1000;
  const maximumRecordCount = 40;
  const horizontalScrollerSelectors = [
    '[data-pino-navigation-scroll]',
    '.slider'
  ];

  let navigationAnchor = null;

  const pageKey = () => `${window.location.pathname}${window.location.search}`;
  const storageKey = () => `${storagePrefix}${pageKey()}`;

  const readRecord = () => {
    try {
      const record = JSON.parse(window.sessionStorage.getItem(storageKey()) || 'null');
      if (!record || Date.now() - record.savedAt > maximumRecordAge) return null;
      return record;
    } catch (_) {
      return null;
    }
  };

  const matchingLinks = (href) => Array.from(document.querySelectorAll('a[href]')).filter((link) => {
    try {
      const url = new URL(link.href, window.location.href);
      return `${url.pathname}${url.search}${url.hash}` === href;
    } catch (_) {
      return false;
    }
  });

  const describeAnchor = (link) => {
    if (!link) return null;

    try {
      const url = new URL(link.href, window.location.href);
      const href = `${url.pathname}${url.search}${url.hash}`;
      const candidates = matchingLinks(href);
      const index = candidates.indexOf(link);
      if (index < 0) return null;

      return {
        href,
        index,
        viewportTop: Math.round(link.getBoundingClientRect().top)
      };
    } catch (_) {
      return null;
    }
  };

  const collectScrollerPositions = () => {
    const positions = [];

    horizontalScrollerSelectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((element, index) => {
        if (element.scrollWidth <= element.clientWidth + 1 && element.scrollHeight <= element.clientHeight + 1) return;
        positions.push({ selector, index, left: element.scrollLeft, top: element.scrollTop });
      });
    });

    return positions;
  };

  const collectInterfaceState = () => ({
    interfaces: Array.from(document.querySelectorAll('[data-pino-navigation-interface]')).map((root, index) => {
      const expandedClass = root.dataset.pinoNavigationExpandedClass;
      return {
        id: root.id || null,
        index,
        expanded: Boolean(expandedClass && root.classList.contains(expandedClass))
      };
    })
  });

  const restoreInterfaceState = (state) => {
    const savedCollections = Array.isArray(state)
      ? state.map((saved) => ({ ...saved, expanded: saved.viewingAll }))
      : state && (state.interfaces || state.designerCollections);
    if (!Array.isArray(savedCollections)) return;

    const roots = Array.from(document.querySelectorAll('[data-pino-navigation-interface]'));
    savedCollections.forEach((saved) => {
      const byId = saved.id ? document.getElementById(saved.id) : null;
      const root = byId && roots.includes(byId) ? byId : roots[saved.index];
      if (!root) return;

      const expanded = Boolean(saved.expanded);
      const expandedClass = root.dataset.pinoNavigationExpandedClass;
      if (expandedClass) root.classList.toggle(expandedClass, expanded);
      const button = root.querySelector('[data-pino-navigation-interface-toggle]');
      if (!button) return;

      button.setAttribute('aria-expanded', String(expanded));
      const label = expanded ? button.dataset.expandedLabel : button.dataset.collapsedLabel;
      if (label) button.textContent = label;
    });
  };

  const pruneRecords = () => {
    try {
      const records = [];
      for (let index = 0; index < window.sessionStorage.length; index += 1) {
        const key = window.sessionStorage.key(index);
        if (!key || !key.startsWith(storagePrefix)) continue;

        let savedAt = 0;
        try {
          savedAt = JSON.parse(window.sessionStorage.getItem(key) || '{}').savedAt || 0;
        } catch (_) {}
        records.push({ key, savedAt });
      }

      records
        .sort((first, second) => second.savedAt - first.savedAt)
        .slice(maximumRecordCount)
        .forEach((record) => window.sessionStorage.removeItem(record.key));
    } catch (_) {}
  };

  const savePosition = (link = navigationAnchor) => {
    try {
      window.sessionStorage.setItem(storageKey(), JSON.stringify({
        savedAt: Date.now(),
        x: window.scrollX,
        y: window.scrollY,
        viewportWidth: window.innerWidth,
        anchor: describeAnchor(link),
        scrollers: collectScrollerPositions(),
        interfaceState: collectInterfaceState()
      }));
      pruneRecords();
    } catch (_) {}
  };

  const restoreScrollers = (positions) => {
    if (!Array.isArray(positions)) return;

    positions.forEach((position) => {
      const element = document.querySelectorAll(position.selector)[position.index];
      if (!element) return;
      element.scrollLeft = position.left || 0;
      element.scrollTop = position.top || 0;
    });
  };

  const anchorTargetY = (record) => {
    if (!record.anchor || Math.abs((record.viewportWidth || window.innerWidth) - window.innerWidth) > 40) return null;
    const link = matchingLinks(record.anchor.href)[record.anchor.index];
    if (!link) return null;
    return window.scrollY + link.getBoundingClientRect().top - record.anchor.viewportTop;
  };

  const applyPosition = (record) => {
    restoreInterfaceState(record.interfaceState);
    restoreScrollers(record.scrollers);
    const anchoredY = anchorTargetY(record);
    const targetY = anchoredY === null ? record.y || 0 : anchoredY;
    const documentScrollBehavior = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = 'auto';
    window.scrollTo({ left: record.x || 0, top: Math.max(0, targetY), behavior: 'instant' });
    document.documentElement.style.scrollBehavior = documentScrollBehavior;
  };

  const navigationIsHistoryTraversal = (event) => {
    if (event.persisted) return true;
    const navigation = window.performance && window.performance.getEntriesByType
      ? window.performance.getEntriesByType('navigation')[0]
      : null;
    return Boolean(navigation && navigation.type === 'back_forward');
  };

  const restorePosition = (event) => {
    if (!navigationIsHistoryTraversal(event)) return;
    const record = readRecord();
    if (!record) return;

    const applyWhenLayoutIsReady = () => {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => applyPosition(record));
      });
    };

    if (document.fonts?.ready) document.fonts.ready.then(applyWhenLayoutIsReady);
    else applyWhenLayoutIsReady();
  };

  const isNormalInternalNavigation = (event, link) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      link.target === '_blank' ||
      link.hasAttribute('download') ||
      link.hasAttribute('data-pino-scroll-target')
    ) return false;

    try {
      const destination = new URL(link.href, window.location.href);
      if (destination.origin !== window.location.origin) return false;
      if (destination.pathname === window.location.pathname && destination.search === window.location.search && destination.hash) return false;
      return destination.protocol === 'http:' || destination.protocol === 'https:';
    } catch (_) {
      return false;
    }
  };

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href]');
    if (!link || !isNormalInternalNavigation(event, link)) return;
    navigationAnchor = link;
    savePosition(link);
  }, true);

  document.addEventListener('click', (event) => {
    const backLink = event.target.closest('[data-pino-context-back]');
    if (!backLink || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    let hasInternalPreviousPage = false;
    try {
      hasInternalPreviousPage = Boolean(document.referrer) && new URL(document.referrer).origin === window.location.origin;
    } catch (_) {}
    if (!hasInternalPreviousPage || window.history.length < 2) return;

    event.preventDefault();
    window.history.back();
  });

  if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
  window.addEventListener('pageshow', restorePosition);
})();
